import React, { useEffect, useRef, useReducer } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Field from './Field';
import { ConnectedPaxFragment } from '../MobileFragments/PaxFragment';
import { totalPax, isTrainsProductActive, totalTrainPax, getTripType } from '../../../redux/currentSearch/selectors';
import { ABUtils, isAgeSelectorEnabled, isTrainsRoundTripDiscountCard, SessionStorageUtils, TrackingUtils } from '../../../utils';
import { FieldType, TripType, TrainPaxErrorsProps, TrainPaxErrorsPropsDefaults } from '../../../models';

const PaxFieldSeparator = ({ showTitle, isTrainsProduct }) => {
    return (showTitle && !isTrainsProduct) ? (
        <React.Fragment>
            <div className="odf-stroke-primary odf-space-outer-top-l odf-space-outer-bottom-m" />
        </React.Fragment>
    ): <></>;
};

const getTrainValidationState = (trainPaxErrors) => {
    const hasTrainPaxErrors = (trainPaxErrors?.paxSelectorLevelError || trainPaxErrors?.paxAgeFieldLevelError);

    return hasTrainPaxErrors ? (trainPaxErrors.paxSelectorLevelError || trainPaxErrors.paxAgeFieldLevelError) : '';
};


const getPaxCount = (isTrainsProduct, trainsPax, pax) => {
    return isTrainsProduct ? trainsPax : pax;
};

const shouldDisplayErrors = (showErrors, hasSubmittedPax) => showErrors || hasSubmittedPax;

const usePaxStateProperty = (propertyName, enabled = true) => {
    const [, forceRender] = useReducer((tick) => tick + 1, 0);
    useEffect(() => {
        if (!enabled || !window.paxState) return undefined;
        return window.paxState.subscribe((changedProperty) => {
            if (changedProperty === propertyName) forceRender();
        });
    }, [propertyName, enabled]);
    if (!enabled || !window.paxState) return undefined;
    return window.paxState.getState()[propertyName];
};

const useDiscountCardContent = ({ isTrainsProduct, isRoundTrip, isRailsRoundTripDiscountCard, paxCount, t, baseLabel }) => {
    const { railsDiscountCards } = ABUtils.getABs();
    const enabled = isTrainsProduct && railsDiscountCards && (!isRoundTrip || isRailsRoundTripDiscountCard);
    const isSinglePax = paxCount === 1;
    const shouldCheckDiscount = enabled && isSinglePax;
    const paxStateApplied = Boolean(usePaxStateProperty('discountCardApplied', shouldCheckDiscount));

    const hasStoredCard = shouldCheckDiscount && Boolean(SessionStorageUtils.getJSON('train-discount-card'));

    const discountCardApplied = paxStateApplied || hasStoredCard;

    const _discountLoadedSent = useRef(false);
    useEffect(() => {
        if (!enabled || _discountLoadedSent.current) return;
        TrackingUtils.trackEvent({ action: TrackingUtils.ACTION.TRAINS, label: 'searcher_discount_card_loaded' });
        _discountLoadedSent.current = true;
    }, [enabled]);

    useEffect(() => {
        if (!shouldCheckDiscount || !window.paxState) return;
        if (hasStoredCard && !window.paxState.getState().discountCardApplied) {
            window.paxState.discountCardApplied = true;
        }
    }, [shouldCheckDiscount, hasStoredCard]);

    if (!enabled) return null;
    const key = discountCardApplied ? 'trains.discountCards.card.added.small' : 'trains.discountCards.card.add';

    const handleDiscountCardClick = () => {
        TrackingUtils.trackEvent({ action: TrackingUtils.ACTION.TRAINS, label: 'searcher_discount_card_click' });
    };

    return (
        <>
            <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{`${baseLabel} |`}</span>
            <span
                data-testid="pax-discount-status"
                style={{
                    marginLeft: '6px',
                    color: discountCardApplied ? undefined : '#909090',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    flexShrink: 1,
                    display: 'inline-block',
                }}
                onClick={handleDiscountCardClick}
            >
                {t(key)}
            </span>
        </>
    );
};

export const PaxField = ({
    pax,
    trainsPax,
    showTitle,
    isHotelOptionEnabled,
    trainPaxErrors,
    trainPaxObj,
    showErrors,
}) => {
    const { t } = useTranslation();
    const isTrainsProduct = useSelector((state) => isTrainsProductActive(state));
    const [hasSubmittedPax, setHasSubmittedPax] = React.useState(false);
    const tripType = useSelector((state) => getTripType(state));
    const isRoundTrip = tripType === TripType.ROUND_TRIP;
    const { isCssVariationMobile } = ABUtils.getABs();
    const isRailsRoundTripDiscountCard = isTrainsRoundTripDiscountCard({ isTrainsProduct, tripType });
    const shouldUseTrainPaxInLabel = isTrainsProduct && trainPaxObj?.youth === 1 && pax === 1;
    const errorMessage = isTrainsProduct ? getTrainValidationState(trainPaxErrors) : '';

    const paxLabelKey = shouldUseTrainPaxInLabel ? 'paxSelector.trains.passengers' : 'flightsManager.passengers';
    const paxCount = getPaxCount(isTrainsProduct, trainsPax, pax);
    const paxLabel = t(paxLabelKey, { count: paxCount });
    const discountCardContent = useDiscountCardContent({
        isTrainsProduct,
        isRoundTrip,
        isRailsRoundTripDiscountCard,
        paxCount,
        t,
        baseLabel: paxLabel,
    });
    const showPaxTitle = !isRoundTrip || isRailsRoundTripDiscountCard;
    const placeHolder = isCssVariationMobile && showPaxTitle ? t('flightsManager.selection.pax.title') : '';

    useEffect(() => {
        if (isTrainsProduct && !errorMessage) setHasSubmittedPax(false);
    }, [isTrainsProduct, errorMessage]);

    // Sync Redux pax to `window.paxState`
    useEffect(() => {
        const shouldSync = isTrainsProduct && isCssVariationMobile && window.paxState && isAgeSelectorEnabled();
        if (!shouldSync) return;

        const { seniors = 0, adults = 1, youth = 0, children = 0 } = trainPaxObj || {};
        Object.assign(window.paxState, { seniors, adults, youth, children });
    }, [isTrainsProduct, isCssVariationMobile, trainPaxObj]);

    return (
        <React.Fragment>
            <PaxFieldSeparator showTitle={showTitle} isTrainsProduct={isTrainsProduct} />
            <Field
                icon="pax"
                id={FieldType.PAX}
                value={paxLabel}
                placeholder={placeHolder}
                errorMessage={shouldDisplayErrors(showErrors, hasSubmittedPax) ? errorMessage : ''}
                renderValue={discountCardContent}
            />
            <ConnectedPaxFragment
                isHotelOptionEnabled={isHotelOptionEnabled}
                hasSubmittedPax={hasSubmittedPax}
                setHasSubmittedPax={setHasSubmittedPax}
            />
        </React.Fragment>
    );
};

PaxField.propTypes = {
    pax: PropTypes.number,
    trainsPax: PropTypes.number,
    trainPaxObj: PropTypes.object,
    showTitle: PropTypes.bool.isRequired,
    trainPaxErrors: TrainPaxErrorsProps,
    showErrors: PropTypes.bool,
};

PaxField.defaultProps = {
    pax: 1,
    trainsPax: 1,
    trainPaxObj: {},
    trainPaxErrors: TrainPaxErrorsPropsDefaults,
    showErrors: false,
};

const mapStateToProps = (state) => ({
    pax: totalPax(state),
    trainsPax: totalTrainPax(state),
    trainPaxObj: state.currentSearch.trainPax || {},
    showTitle: state.currentSearch.tripType !== TripType.ROUND_TRIP || isTrainsRoundTripDiscountCard({
        isTrainsProduct: isTrainsProductActive(state),
        tripType: state.currentSearch.tripType,
    }),
    trainPaxErrors: state.currentSearch.trainPaxErrors || {},
    showErrors: state.currentSearch.segments?.some((segment) => segment.showErrors) || false,
});

export const ConnectedPaxField = connect(mapStateToProps)(PaxField);
